import { useState, useEffect, type MouseEvent, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { Box, Divider, Typography, Stack, MenuItem, Avatar, IconButton, Popover } from '@mui/material';

import { useGetUserByEmailQuery } from '../../../slices/apis/userApi';

interface MenuOption {
	label: string;
	route: string;
}

// ----------------------------------------------------------------------

const MENU_OPTIONS: MenuOption[] = [
	{
		label: 'Home',
		route: '/dashboard/requests',
	},
	{
		label: 'Profile',
		route: '/dashboard/user-profile',
	},
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
	const [open, setOpen] = useState<HTMLElement | null>(null);
	const { user: auth0User, logout, isAuthenticated } = useAuth0();
	const navigate = useNavigate();

	const { data: user } = useGetUserByEmailQuery(auth0User?.email);

	const handleOpen = (event: MouseEvent<HTMLElement>) => {
		setOpen(event.currentTarget);
	};

	const handleClose = () => {
		setOpen(null);
	};

	const handleMenuItemClick = useCallback(
		(e: React.MouseEvent<HTMLLIElement>) => {
			setOpen(null);
			navigate(e.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	const handleLogout = () => {
		setOpen(null);
		logout({
			logoutParams: {
				returnTo: window.location.origin,
			},
		});
	};

	useEffect(() => {
		if (isAuthenticated === false) {
			logout({
				logoutParams: {
					returnTo: window.location.origin,
				},
			});
		}
	}, [isAuthenticated, logout]);

	return (
		<>
			<IconButton
				onClick={handleOpen}
				sx={{
					p: 0,
					...(open && {
						'&:before': {
							zIndex: 1,
							content: "''",
							width: '100%',
							height: '100%',
							borderRadius: '50%',
							position: 'absolute',
							bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
						},
					}),
				}}
			>
				<Avatar src={auth0User?.picture} alt="photoURL" />
			</IconButton>

			<Popover
				open={Boolean(open)}
				anchorEl={open}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				PaperProps={{
					sx: {
						p: 0,
						mt: 1.5,
						ml: 0.75,
						width: 180,
						'& .MuiMenuItem-root': {
							typography: 'body2',
							borderRadius: 0.75,
						},
					},
				}}
			>
				<Box sx={{ my: 1.5, px: 2.5 }}>
					<Typography variant="subtitle2" noWrap>
						{user?.firstname}&nbsp;&nbsp;{user?.lastname}
					</Typography>
					<Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
						{auth0User?.email}
					</Typography>
				</Box>

				<Divider sx={{ borderStyle: 'dashed' }} />

				<Stack sx={{ p: 1 }}>
					{MENU_OPTIONS.map((option) => (
						<MenuItem key={option.label} data-route={option.route} onClick={handleMenuItemClick}>
							{option.label}
						</MenuItem>
					))}
				</Stack>

				<Divider sx={{ borderStyle: 'dashed' }} />

				<MenuItem onClick={handleLogout} sx={{ m: 1 }}>
					Logout
				</MenuItem>
			</Popover>
		</>
	);
}
